import { RackingArea } from '@thive/fddm-types';
import { Stack } from '@mui/system';
import { memo, useCallback, useMemo } from 'react';
import { Switch } from '@mui/material';

import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useHasOmniVehicles } from '@/modules/vehicles/hooks/useHasOmniVehicles';
import { CrabSettingInput } from '../common/CrabSettingInput';

type Props = {
  selectedRackingArea: RackingArea;
};

function RackingPoiPreferencesComponent({ selectedRackingArea }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const hasOmniVehicles = useHasOmniVehicles();
  const crabSetting = selectedRackingArea.crabSetting ?? 0;
  const showCrabSetting = hasOmniVehicles && selectedRackingArea.rackType === 'ONE_SIDED_ALONGSIDE';
  const showParkingSetting = false;

  const handleCrabChange = useCallback(
    (value: number) => {
      fddmSvc.setPreference('rackingArea.crabSetting', value);
      fddmSvc.updateArea({
        ...selectedRackingArea,
        crabSetting: value,
      });
      const smoothHeadingValue =
        value !== 0 ? true : fddmSvc.getPreference('connection.smoothHeading');
      const relatedConnections = useFloorplanDesignStore
        .getState()
        .interAreaConnectionActions.getRelatedTo([selectedRackingArea.id]);
      if (relatedConnections.length > 0) {
        fddmSvc.updateInterAreaConnections(
          relatedConnections.map((conn) => ({
            ...conn,
            aToBConnections: conn.aToBConnections.map((c) => ({
              ...c,
              smoothHeading: smoothHeadingValue,
            })),
            bToAConnections: conn.bToAConnections.map((c) => ({
              ...c,
              smoothHeading: smoothHeadingValue,
            })),
          })),
        );
      }
    },
    [fddmSvc, selectedRackingArea],
  );

  const onParkingChange = useCallback(
    (_, newValue: boolean) => {
      fddmSvc.setPreference('rackingArea.canBeUsedForParking', newValue);

      const parkingExtension = newValue
        ? {
            priority: fddmSvc.getPreference('rackingArea.parkingExtension.priority'),
          }
        : undefined;

      fddmSvc.updateArea({
        ...selectedRackingArea,
        canBeUsedForParking: newValue,
        parkingExtension,
      });
    },
    [fddmSvc, selectedRackingArea],
  );

  const onParkingPriorityChange = useCallback(
    (newValue: number) => {
      if (Number.isNaN(newValue)) return;

      fddmSvc.setPreference('rackingArea.parkingExtension.priority', newValue);

      fddmSvc.updateArea({
        ...selectedRackingArea,
        parkingExtension: {
          priority: newValue,
        },
      });
    },
    [fddmSvc, selectedRackingArea],
  );

  return (
    <Stack spacing={1}>
      {showCrabSetting && (
        <PropertiesLabel i18nkey='interface:properties.area.crab_setting'>
          <CrabSettingInput value={crabSetting} onChange={handleCrabChange} />
        </PropertiesLabel>
      )}
      {showParkingSetting && (
        <PropertiesLabel i18nkey='interface:properties.area.can_be_used_for_parking'>
          <Switch
            checked={selectedRackingArea.canBeUsedForParking}
            onChange={onParkingChange}
          />
        </PropertiesLabel>
      )}
      {showParkingSetting &&
        selectedRackingArea.canBeUsedForParking === true &&
        selectedRackingArea.parkingExtension !== undefined && (
          <PropertiesLabel i18nkey='interface:properties.area.priority'>
            <NumberInput
              value={selectedRackingArea.parkingExtension.priority}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onParkingPriorityChange}
            />
          </PropertiesLabel>
        )}
    </Stack>
  );
};

export const RackingPoiPreferences = memo(RackingPoiPreferencesComponent);